const escapeXml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildProxyUrl = (fileId, sizeHint = 'w1600-h1600') => {
  if (!fileId) return '';
  return `/api/image-proxy?id=${encodeURIComponent(fileId)}&sz=${encodeURIComponent(sizeHint)}`;
};

const extractDriveId = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const rawTrim = raw.trim();
  if (rawTrim.startsWith('/api/image-proxy')) return null;
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
    /uc\?export=view&id=([a-zA-Z0-9_-]+)/,
    /thumbnail\?id=([a-zA-Z0-9_-]+)/,
    /lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/,
    /^([a-zA-Z0-9_-]{25,})$/
  ];
  for (const p of patterns) {
    const m = rawTrim.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
};

const normalizeImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return '';
  const id = extractDriveId(raw);
  if (id) {
    return buildProxyUrl(id, 'w1600-h1600');
  }
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/')) return raw;
  if (raw.startsWith('uploads/')) return `/${raw}`;
  return `/${raw}`;
};

const withCacheBust = (url, blog) => {
  if (!url) return url;
  const stamp = blog?.updatedAt || blog?.createdAt || blog?.publishedAt;
  if (!stamp) return url;
  const value = encodeURIComponent(String(stamp));
  return url.includes('?') ? `${url}&v=${value}` : `${url}?v=${value}`;
};

const hashString = (value = '') => {
  let hash = 2166136261;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const splitTitle = (title = '') => {
  const clean = String(title || '').trim();
  if (!clean) return ['Article', 'DOMOELEC'];
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length <= 4) return [clean, ''];
  const first = words.slice(0, 4).join(' ');
  const second = words.slice(4, 8).join(' ');
  return [first, second];
};

export const getBlogPlaceholderDataUri = (blog) => {
  const key = blog?.slug || blog?.title || String(blog?.id || '');
  const hash = hashString(key);
  const palettes = [
    { a: '#0C1C3B', b: '#12376D', accent: '#54BA66' },
    { a: '#0B1220', b: '#1E3A8A', accent: '#54BA66' },
    { a: '#0D1B3D', b: '#0B2A55', accent: '#2A6EE8' },
    { a: '#061827', b: '#0E3A5B', accent: '#54BA66' }
  ];
  const palette = palettes[hash % palettes.length];
  const title = String(blog?.title || 'Article DOMOELEC');
  const [line1, line2] = splitTitle(title);
  const dateLabel = blog?.publishedAt || blog?.createdAt || blog?.updatedAt ? 'Blog' : 'DOMOELEC';

  const svg = `
    <svg width="1200" height="675" viewBox="0 0 1200 675" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bg" x1="72" y1="44" x2="1128" y2="631" gradientUnits="userSpaceOnUse">
          <stop stop-color="${palette.a}"/>
          <stop offset="1" stop-color="${palette.b}"/>
        </linearGradient>
      </defs>
      <rect width="1200" height="675" rx="28" fill="url(#bg)"/>
      <circle cx="960" cy="170" r="210" fill="${palette.accent}" fill-opacity="0.18"/>
      <circle cx="980" cy="520" r="260" fill="#FFFFFF" fill-opacity="0.08"/>
      <rect x="510" y="54" width="180" height="44" rx="22" fill="#FFFFFF" fill-opacity="0.12"/>
      <text x="600" y="83" text-anchor="middle" fill="#DCE7F4" font-family="Arial, sans-serif" font-size="18" font-weight="700">DOMOELEC</text>
      <text x="600" y="220" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="54" font-weight="800">${escapeXml(line1)}</text>
      ${
        line2
          ? `<text x="600" y="280" text-anchor="middle" fill="#FFFFFF" font-family="Arial, sans-serif" font-size="46" font-weight="800" opacity="0.95">${escapeXml(
              line2
            )}</text>`
          : ''
      }
      <text x="600" y="344" text-anchor="middle" fill="#DCE7F4" font-family="Arial, sans-serif" font-size="22" opacity="0.92">${escapeXml(
        dateLabel
      )} • ${escapeXml(String(blog?.slug || '').slice(0, 28))}</text>
      <rect x="470" y="396" width="260" height="54" rx="16" fill="${palette.accent}"/>
      <text x="600" y="432" text-anchor="middle" fill="#0B1220" font-family="Arial, sans-serif" font-size="22" font-weight="800">Lire l'article</text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export const getBlogCoverSrc = (blog) => {
  const cover = normalizeImageUrl(blog?.coverImageUrl);
  if (cover) return withCacheBust(cover, blog);
  return getBlogPlaceholderDataUri(blog);
};

export const getBlogFallbackSrc = (blog) => getBlogPlaceholderDataUri(blog);
